import React, { useRef, useCallback } from 'react'

const LEVELS = [
  {
    value: 1,
    label: 'A rough week',
    subtitle: 'More setbacks than wins',
    iconKey: 'stormy',
    bgTint: '#FCEBEB',
    accentColor: '#E24B4A',
    titleColor: '#501313',
    subtitleColor: '#791F1F',
  },
  {
    value: 2,
    label: 'A tough week',
    subtitle: 'Felt heavy',
    iconKey: 'cloudy',
    bgTint: '#FAECE7',
    accentColor: '#D85A30',
    titleColor: '#4A1B0C',
    subtitleColor: '#712B13',
  },
  {
    value: 3,
    label: 'A mixed week',
    subtitle: 'Some good, some rough',
    iconKey: 'partly_cloudy',
    bgTint: '#FAEEDA',
    accentColor: '#BA7517',
    titleColor: '#412402',
    subtitleColor: '#854F0B',
  },
  {
    value: 4,
    label: 'A good week',
    subtitle: 'More wins than setbacks',
    iconKey: 'sunny',
    bgTint: '#EAF3DE',
    accentColor: '#639922',
    titleColor: '#173404',
    subtitleColor: '#27500A',
  },
  {
    value: 5,
    label: 'A great week',
    subtitle: 'Everything clicked',
    iconKey: 'sunshine',
    bgTint: '#E1F5EE',
    accentColor: '#1D9E75',
    titleColor: '#04342C',
    subtitleColor: '#0F6E56',
  },
]

export { LEVELS as WEEK_RATING_LEVELS }

function StormyIcon({ selected, style }) {
  const strokeW = selected ? 2 : 1.5
  const cloudFill = selected ? '#5F5E5A' : '#888780'
  const cloudStroke = selected ? '#2C2C2A' : '#5F5E5A'
  const rainColor = selected ? '#185FA5' : '#378ADD'
  const rainW = selected ? 2.2 : 1.8

  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <path
        d="M14 18 Q10 18 10 23 Q6 23 6 28 Q6 33 11 33 L42 33 Q47 33 47 28 Q47 23 42 23 Q42 16 34 16 Q28 12 22 16 Q14 14 14 18 Z"
        fill={cloudFill} stroke={cloudStroke} strokeWidth={strokeW}
      />
      <path d="M15 36 L12 42" stroke={rainColor} strokeWidth={rainW} strokeLinecap="round" />
      <path d="M23 36 L20 42" stroke={rainColor} strokeWidth={rainW} strokeLinecap="round" />
      <path d="M31 36 L28 42" stroke={rainColor} strokeWidth={rainW} strokeLinecap="round" />
      <path d="M39 36 L36 42" stroke={rainColor} strokeWidth={rainW} strokeLinecap="round" />
    </svg>
  )
}

function CloudyIcon({ selected, style }) {
  const strokeW = selected ? 2 : 1.5
  const cloudFill = selected ? '#888780' : '#B4B2A9'
  const cloudStroke = selected ? '#444441' : '#5F5E5A'

  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <path
        d="M14 20 Q10 20 10 25 Q6 25 6 30 Q6 35 11 35 L42 35 Q47 35 47 30 Q47 25 42 25 Q42 18 34 18 Q28 14 22 18 Q14 16 14 20 Z"
        fill={cloudFill} stroke={cloudStroke} strokeWidth={strokeW}
      />
    </svg>
  )
}

function PartlyCloudyIcon({ selected, style }) {
  const strokeW = selected ? 2 : 1.5
  const sunFill = '#FAC775'
  const sunStroke = selected ? '#854F0B' : '#BA7517'
  const cloudFill = selected ? '#B4B2A9' : '#D3D1C7'
  const cloudStroke = selected ? '#444441' : '#5F5E5A'

  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <circle cx="20" cy="20" r="8" fill={sunFill} stroke={sunStroke} strokeWidth={strokeW} />
      <path
        d="M28 22 Q24 22 24 27 Q20 27 20 32 Q20 37 25 37 L46 37 Q51 37 51 32 Q51 27 46 27 Q46 20 38 20 Q32 16 28 22 Z"
        fill={cloudFill} stroke={cloudStroke} strokeWidth={strokeW}
      />
    </svg>
  )
}

function SunnyIcon({ selected, style }) {
  const rayW = selected ? 2 : 1.6
  const sunStrokeW = selected ? 2.2 : 1.8
  const rayColor = selected ? '#854F0B' : '#BA7517'
  const sunFill = '#FAC775'
  const sunStroke = selected ? '#633806' : '#854F0B'

  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <line x1="28" y1="4" x2="28" y2="8" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="28" y1="36" x2="28" y2="40" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="8" y1="22" x2="12" y2="22" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="44" y1="22" x2="48" y2="22" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="14" y1="8" x2="17" y2="11" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="39" y1="33" x2="42" y2="36" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="14" y1="36" x2="17" y2="33" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="39" y1="11" x2="42" y2="8" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <circle cx="28" cy="22" r="10" fill={sunFill} stroke={sunStroke} strokeWidth={sunStrokeW} />
    </svg>
  )
}

function SunshineIcon({ selected, style }) {
  const rayW = selected ? 2.4 : 1.6
  const sunStrokeW = selected ? 2.4 : 1.8
  const rayColor = selected ? '#854F0B' : '#BA7517'
  const sunOuter = '#EF9F27'
  const sunInner = '#FAC775'
  const sunStroke = selected ? '#412402' : '#854F0B'

  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <line x1="28" y1="2" x2="28" y2="6" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="28" y1="38" x2="28" y2="42" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="4" y1="22" x2="8" y2="22" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="48" y1="22" x2="52" y2="22" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="10" y1="4" x2="14" y2="8" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="42" y1="36" x2="46" y2="40" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="10" y1="40" x2="14" y2="36" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <line x1="42" y1="8" x2="46" y2="4" stroke={rayColor} strokeWidth={rayW} strokeLinecap="round" />
      <circle cx="28" cy="22" r="12" fill={sunOuter} stroke={sunStroke} strokeWidth={sunStrokeW} />
      <circle cx="28" cy="22" r="7" fill={sunInner} />
    </svg>
  )
}

function IconFor({ iconKey, selected }) {
  const iconStyle = { width: '100%', height: '100%', display: 'block' }
  switch (iconKey) {
    case 'stormy': return <StormyIcon selected={selected} style={iconStyle} />
    case 'cloudy': return <CloudyIcon selected={selected} style={iconStyle} />
    case 'partly_cloudy': return <PartlyCloudyIcon selected={selected} style={iconStyle} />
    case 'sunny': return <SunnyIcon selected={selected} style={iconStyle} />
    case 'sunshine': return <SunshineIcon selected={selected} style={iconStyle} />
    default: return null
  }
}

export function WeekRatingWeatherScale({ value, onChange, disabled }) {
  const buttonsRef = useRef([])
  const selected = value?.value ?? null
  const selectedLevel = selected ? LEVELS.find((l) => l.value === selected) : null

  const handleClick = useCallback((level) => {
    if (disabled) return
    onChange({ value: level.value, label: level.label })
  }, [disabled, onChange])

  const handleKeyDown = useCallback((e, index) => {
    if (disabled) return
    let nextIndex = null
    if (e.key === 'ArrowRight') {
      nextIndex = Math.min(index + 1, LEVELS.length - 1)
    } else if (e.key === 'ArrowLeft') {
      nextIndex = Math.max(index - 1, 0)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(LEVELS[index])
      return
    } else {
      return
    }
    e.preventDefault()
    buttonsRef.current[nextIndex]?.focus()
  }, [disabled, handleClick])

  const focusableIndex = selected ? LEVELS.findIndex((l) => l.value === selected) : 0

  return (
    <div style={styles.container}>
      {/* Icon row */}
      <div style={styles.iconRow}>
        {LEVELS.map((level, index) => {
          const isSelected = selected === level.value
          const opacity = disabled ? 0.4 : isSelected ? 1 : 0.55
          return (
            <button
              key={level.value}
              ref={(el) => { buttonsRef.current[index] = el }}
              style={{
                ...styles.iconButton,
                opacity,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'opacity var(--duration-fast) ease',
              }}
              onClick={() => handleClick(level)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onMouseEnter={(e) => {
                if (!disabled && !isSelected) e.currentTarget.style.opacity = '0.8'
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isSelected) e.currentTarget.style.opacity = '0.55'
              }}
              disabled={disabled}
              tabIndex={index === focusableIndex ? 0 : -1}
              aria-label={`Rating ${level.value}: ${level.label}`}
              aria-pressed={isSelected}
            >
              <IconFor iconKey={level.iconKey} selected={isSelected} />
            </button>
          )
        })}
      </div>

      {/* Verdict panel */}
      {selectedLevel && (
        <div
          style={{
            ...styles.verdictPanel,
            background: selectedLevel.bgTint,
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ ...styles.accentBar, background: selectedLevel.accentColor }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: selectedLevel.titleColor }}>
              {selectedLevel.label}
            </div>
            <div style={{ fontSize: 11, color: selectedLevel.subtitleColor }}>
              {selectedLevel.subtitle}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 120,
    padding: '12px 14px',
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'clamp(4px, 1.5vw, 12px)',
    padding: '0 clamp(2px, 1vw, 8px)',
    width: '100%',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: '20%',
    aspectRatio: '56 / 44',
  },
  verdictPanel: {
    marginTop: 14,
    padding: '10px 14px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  accentBar: {
    width: 6,
    height: 24,
    borderRadius: 3,
    flexShrink: 0,
  },
}
